import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Plus, X, Search, Grid, List, MoreVertical, Edit2, Trash2, Download, Eye, FileText, Image as ImageIcon, File, Music, Video, Filter, User, Users as UsersIcon } from 'lucide-react';
import { fetchDocuments, createDocument, updateDocument, deleteDocument } from '../services/documentsApi';
import MemberAvatar from './MemberAvatar';
import MemberSelector from './MemberSelector';
import UserAvatar from './UserAvatar';
import Premium from './Premium';
import NavBar from './NavBar';

const Documents = () => {
    const navigate = useNavigate();
    const [showPremiumModal, setShowPremiumModal] = useState(false);
    const [families, setFamilies] = useState([]);
    const [selectedFamily, setSelectedFamily] = useState(null);
    const [viewMode, setViewMode] = useState('grid'); // grid or list
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterCategory, setFilterCategory] = useState('all');
    const [showFilterMenu, setShowFilterMenu] = useState(false);
    const [editingDocument, setEditingDocument] = useState(null);
    const [openMenuId, setOpenMenuId] = useState(null);
    
    // Form states
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState('Uncategorized');
    const [belongsTo, setBelongsTo] = useState('Family'); // 'Family' or 'Individual'
    const [belongsToMember, setBelongsToMember] = useState('');
    const [file, setFile] = useState(null);
    const [filePreview, setFilePreview] = useState(null);
    const [sharedWith, setSharedWith] = useState([]);
    const [uploadError, setUploadError] = useState('');
    
    // Documents data
    const [documents, setDocuments] = useState([]);
    
    const filterMenuRef = useRef(null);
    const fileInputRef = useRef(null);

    const categories = [
        'Uncategorized',
        'Driving License',
        'Insurance',
        'Passport',
        'Invoice',
        'Personal Card',
        'Bank',
        'Medical Record',
        'Business Card',
        'Contract',
        'Product',
        'Electricity/Gas',
        'Education',
        'Birth Certificate'
    ];

    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB in bytes

    useEffect(() => {
        const loadData = async () => {
            try {
                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
                const response = await fetch(`${apiUrl}/v1/families`, {
                    credentials: 'include'
                });
                
                if (!response.ok) {
                    throw new Error('Failed to load families');
                }
                
                const familiesData = await response.json();
                setFamilies(familiesData);

                const selectedFamilyId = localStorage.getItem('selectedFamilyId');
                let familyToSelect = null;
                
                if (selectedFamilyId && familiesData.length > 0) {
                    familyToSelect = familiesData.find(f => f.id === selectedFamilyId);
                }
                
                if (!familyToSelect && familiesData.length === 1) {
                    familyToSelect = familiesData[0];
                    localStorage.setItem('selectedFamilyId', familiesData[0].id);
                }
                
                if (!familyToSelect && familiesData.length > 0) {
                    localStorage.removeItem('selectedFamilyId');
                }
                
                setSelectedFamily(familyToSelect);
            } catch (error) {
                console.error('Error loading families:', error);
            }
        };
        loadData();
    }, []);

    // Load documents when family changes
    useEffect(() => {
        const loadDocs = async () => {
            if (!selectedFamily) return;
            
            try {
                const response = await fetchDocuments(selectedFamily.id);
                setDocuments(response.items || []);
            } catch (error) {
                console.error('Error loading documents:', error);
            }
        };
        loadDocs();
    }, [selectedFamily]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (filterMenuRef.current && !filterMenuRef.current.contains(event.target)) {
                setShowFilterMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Documents are now handled via API, no need for local storage

    useEffect(() => {
        if (showUploadModal || showPremiumModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
    }, [showUploadModal, showPremiumModal]);

    const handleLogout = () => {
        localStorage.removeItem('hasLoggedIn');
        localStorage.removeItem('selectedFamilyId');
        navigate('/login');
    };

    const getFileType = (filename) => {
        const ext = filename.split('.').pop().toLowerCase();
        
        // Images
        if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(ext)) {
            return 'image';
        }
        // Videos
        if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm'].includes(ext)) {
            return 'video';
        }
        // Audio
        if (['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a'].includes(ext)) {
            return 'audio';
        }
        // Documents
        if (['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'txt', 'html', 'xml'].includes(ext)) {
            return 'document';
        }
        
        return 'file';
    };

    const getFileIcon = (fileType) => {
        switch (fileType) {
            case 'image':
                return <ImageIcon className="w-8 h-8 text-blue-500" />;
            case 'video':
                return <Video className="w-8 h-8 text-purple-500" />;
            case 'audio':
                return <Music className="w-8 h-8 text-pink-500" />;
            case 'document':
                return <FileText className="w-8 h-8 text-orange-500" />;
            default:
                return <File className="w-8 h-8 text-gray-500" />;
        }
    };

    const handleFileSelect = (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;

        setUploadError('');

        // Check for zip files
        const ext = selectedFile.name.split('.').pop().toLowerCase();
        if (ext === 'zip' || ext === 'rar' || ext === '7z') {
            setUploadError('Zip files are not allowed');
            setFile(null);
            setFilePreview(null);
            e.target.value = '';
            return;
        }

        // Check file size
        if (selectedFile.size > MAX_FILE_SIZE) {
            setUploadError('File size exceeds 10 MB limit');
            setFile(null);
            setFilePreview(null);
            e.target.value = '';
            return;
        }

        setFile(selectedFile);

        // Create preview for images
        const fileType = getFileType(selectedFile.name);
        if (fileType === 'image') {
            const reader = new FileReader();
            reader.onloadend = () => {
                setFilePreview(reader.result);
            };
            reader.readAsDataURL(selectedFile);
        } else {
            setFilePreview(null);
        }
    };

    const handleUploadDocument = async () => {
        if (!name || !category || !file) {
            setUploadError('Please fill in all required fields and select a file');
            return;
        }

        if (belongsTo === 'Individual' && !belongsToMember) {
            setUploadError('Please select a family member');
            return;
        }

        if (!selectedFamily) {
            setUploadError('Please select a family first');
            return;
        }

        const fileType = getFileType(file.name);
        const fileSize = (file.size / (1024 * 1024)).toFixed(2); // Size in MB

        try {
            // Map frontend fields to backend schema
            const documentData = {
                name: name,
                description: description || null,
                category: category,
                file_name: file.name,
                file_type: fileType,
                file_size: fileSize,
                file_url: filePreview || URL.createObjectURL(file),
                belongs_to_member: belongsTo === 'Individual' ? belongsToMember : null,
                shared_with: sharedWith
            };

            if (editingDocument) {
                const updated = await updateDocument(selectedFamily.id, editingDocument.id, documentData);
                setDocuments(documents.map(d => d.id === editingDocument.id ? updated : d));
            } else {
                const created = await createDocument(selectedFamily.id, documentData);
                setDocuments([...documents, created]);
            }

            resetForm();
        } catch (error) {
            console.error('Error saving document:', error);
            setUploadError(`Failed to save document: ${error.message}`);
        }
    };

    const handleEditDocument = (doc) => {
        setEditingDocument(doc);
        setName(doc.name);
        setDescription(doc.description || '');
        setCategory(doc.category);
        setBelongsTo(doc.belongsTo || 'Family');
        setBelongsToMember(doc.belongsToMember || '');
        setSharedWith(doc.sharedWith || []);
        setFilePreview(doc.fileUrl);
        // Note: We can't restore the actual File object, so we'll just show the preview
        setShowUploadModal(true);
        setOpenMenuId(null);
    };

    const handleDeleteDocument = async (id) => {
        if (!window.confirm('Are you sure you want to delete this document?')) {
            return;
        }

        if (!selectedFamily) {
            alert('Please select a family first');
            return;
        }

        try {
            await deleteDocument(selectedFamily.id, id);
            setDocuments(documents.filter(d => d.id !== id));
        } catch (error) {
            console.error('Error deleting document:', error);
            alert(`Failed to delete document: ${error.message}`);
        }
        
        setOpenMenuId(null);
    };

    const handleDownload = (doc) => {
        // Create a temporary link and trigger download
        const link = document.createElement('a');
        link.href = doc.fileUrl;
        link.download = doc.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const resetForm = () => {
        setName('');
        setDescription('');
        setCategory('Uncategorized');
        setBelongsTo('Family');
        setBelongsToMember('');
        setFile(null);
        setFilePreview(null);
        setSharedWith([]);
        setEditingDocument(null);
        setShowUploadModal(false);
        setUploadError('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const getFamilyMembers = () => {
        if (!selectedFamily || !selectedFamily.members) return [];
        return selectedFamily.members.filter(member => member && typeof member === 'object' && member.name);
    };

    const toggleMemberAccess = (member) => {
        const memberName = typeof member === 'object' ? member.name : member;
        if (sharedWith.includes(memberName)) {
            setSharedWith(sharedWith.filter(m => m !== memberName));
        } else {
            setSharedWith([...sharedWith, memberName]);
        }
    };

    const getFilteredDocuments = () => {
        let filtered = [...documents];

        if (searchQuery) {
            filtered = filtered.filter(d => 
                d.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                d.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                d.fileName.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        if (filterCategory !== 'all') {
            filtered = filtered.filter(d => d.category === filterCategory);
        }

        return filtered;
    };

    const formatFileSize = (size) => {
        return size;
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <NavBar
                families={families}
                selectedFamily={selectedFamily}
                setSelectedFamily={setSelectedFamily}
                setShowPremiumModal={setShowPremiumModal}
                handleLogout={handleLogout}
            />

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Breadcrumb */}
                <div className="mb-4 flex items-center gap-2 text-sm">
                    <button 
                        onClick={() => navigate('/dashboard')}
                        className="text-gray-600 hover:text-primary-blue transition-colors"
                    >
                        Dashboard
                    </button>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-900 font-medium">Documents</span>
                </div>

                {/* Header */}
                <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <h2 className="text-3xl font-bold text-gray-900">Documents</h2>
                    <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
                        {/* Search */}
                        <div className="relative flex-1 sm:flex-none">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search documents..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent w-full sm:w-64"
                            />
                        </div>

                        {/* Filter */}
                        <div className="relative" ref={filterMenuRef}>
                            <button
                                onClick={() => setShowFilterMenu(!showFilterMenu)}
                                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                <Filter className="w-5 h-5" />
                                Filter
                            </button>

                            {showFilterMenu && (
                                <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-10 max-h-96 overflow-y-auto">
                                    <button
                                        onClick={() => {
                                            setFilterCategory('all');
                                            setShowFilterMenu(false);
                                        }}
                                        className={`w-full px-4 py-2 text-left hover:bg-gray-50 ${filterCategory === 'all' ? 'bg-blue-50 text-primary-blue font-semibold' : 'text-gray-700'}`}
                                    >
                                        All Categories
                                    </button>
                                    {categories.map((cat) => (
                                        <button
                                            key={cat}
                                            onClick={() => {
                                                setFilterCategory(cat);
                                                setShowFilterMenu(false);
                                            }}
                                            className={`w-full px-4 py-2 text-left hover:bg-gray-50 ${filterCategory === cat ? 'bg-blue-50 text-primary-blue font-semibold' : 'text-gray-700'}`}
                                        >
                                            {cat}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* View Toggle */}
                        <div className="flex items-center gap-1 border border-gray-300 rounded-lg p-1">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-primary-blue text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                                title="Grid view"
                            >
                                <Grid className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-2 rounded ${viewMode === 'list' ? 'bg-primary-blue text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                                title="List view"
                            >
                                <List className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Upload Button */}
                        <button
                            onClick={() => setShowUploadModal(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-primary-blue text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                        >
                            <Plus className="w-5 h-5" />
                            Upload
                        </button>
                    </div>
                </div>

                {/* Documents Display */}
                {getFilteredDocuments().length === 0 ? (
                    <div className="bg-white rounded-xl shadow-md p-12 text-center">
                        <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500">No documents yet. Click "Upload" to get started!</p>
                    </div>
                ) : viewMode === 'grid' ? (
                    /* Grid View */
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {getFilteredDocuments().map((doc) => (
                            <div key={doc.id} className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow overflow-hidden">
                                {/* Preview */}
                                <div className="h-48 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center relative">
                                    {doc.fileType === 'image' ? (
                                        <img src={doc.fileUrl} alt={doc.name} className="w-full h-full object-cover" />
                                    ) : (
                                        getFileIcon(doc.fileType)
                                    )}
                                    
                                    {/* Category Badge */}
                                    <div className="absolute top-2 left-2 bg-white/90 backdrop-blur-sm px-2 py-1 rounded text-xs font-medium text-gray-700">
                                        {doc.category}
                                    </div>

                                    {/* Menu */}
                                    <div className="absolute top-2 right-2">
                                        <button
                                            onClick={() => setOpenMenuId(openMenuId === doc.id ? null : doc.id)}
                                            className="p-1.5 bg-white/90 backdrop-blur-sm rounded-lg hover:bg-white transition-colors"
                                        >
                                            <MoreVertical className="w-4 h-4 text-gray-600" />
                                        </button>

                                        {openMenuId === doc.id && (
                                            <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-10">
                                                <button
                                                    onClick={() => handleDownload(doc)}
                                                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                                >
                                                    <Download className="w-4 h-4" />
                                                    <span>Download</span>
                                                </button>
                                                <button
                                                    onClick={() => handleEditDocument(doc)}
                                                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                    <span>Edit</span>
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteDocument(doc.id)}
                                                    className="w-full px-4 py-2 text-left hover:bg-red-50 flex items-center gap-3 text-red-600"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                    <span>Delete</span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Info */}
                                <div className="p-4">
                                    <h3 className="font-semibold text-gray-900 truncate mb-1">{doc.name}</h3>
                                    {doc.description && (
                                        <p className="text-sm text-gray-600 line-clamp-2 mb-2">{doc.description}</p>
                                    )}
                                    <div className="flex items-center justify-between text-xs text-gray-500">
                                        <span>{doc.fileSize}</span>
                                        <span>{doc.fileName.split('.').pop().toUpperCase()}</span>
                                    </div>
                                    
                                    {/* Belongs To */}
                                    {doc.belongsTo && (
                                        <div className="mt-2">
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-medium rounded">
                                                {doc.belongsTo === 'Individual' && doc.belongsToMember ? (
                                                    <>
                                                        <User className="w-3 h-3" />
                                                        {doc.belongsToMember}
                                                    </>
                                                ) : (
                                                    <>
                                                        <UsersIcon className="w-3 h-3" />
                                                        Family
                                                    </>
                                                )}
                                            </span>
                                        </div>
                                    )}
                                    
                                    {/* Shared With */}
                                    {doc.sharedWith && doc.sharedWith.length > 0 && (
                                        <div className="flex items-center gap-1 mt-3">
                                            <div className="flex -space-x-2">
                                                {doc.sharedWith.slice(0, 3).map((member, index) => (
                                                    <div key={index} className="relative">
                                                        <MemberAvatar name={member} size="xs" className="border-2 border-white" />
                                                    </div>
                                                ))}
                                                {doc.sharedWith.length > 3 && (
                                                    <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center text-gray-700 text-xs font-bold border-2 border-white">
                                                        +{doc.sharedWith.length - 3}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    /* List View */
                    <div className="space-y-3">
                        {getFilteredDocuments().map((doc) => (
                            <div key={doc.id} className="bg-white rounded-xl shadow-md p-4 hover:shadow-lg transition-shadow">
                                <div className="flex items-center gap-4">
                                    {/* Icon/Preview */}
                                    <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                                        {doc.fileType === 'image' ? (
                                            <img src={doc.fileUrl} alt={doc.name} className="w-full h-full object-cover" />
                                        ) : (
                                            getFileIcon(doc.fileType)
                                        )}
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-lg font-semibold text-gray-900 truncate">{doc.name}</h3>
                                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 mt-1">
                                            <span className="px-2 py-0.5 bg-gray-100 rounded text-xs font-medium">{doc.category}</span>
                                            {doc.belongsTo && (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-medium rounded">
                                                    {doc.belongsTo === 'Individual' && doc.belongsToMember ? (
                                                        <>
                                                            <User className="w-3 h-3" />
                                                            {doc.belongsToMember}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <UsersIcon className="w-3 h-3" />
                                                            Family
                                                        </>
                                                    )}
                                                </span>
                                            )}
                                            <span>{doc.fileName}</span>
                                            <span>{doc.fileSize}</span>
                                            <span>Uploaded by {doc.uploadedBy}</span>
                                        </div>
                                        {doc.description && (
                                            <p className="text-sm text-gray-600 mt-1 line-clamp-1">{doc.description}</p>
                                        )}
                                    </div>

                                    {/* Shared With */}
                                    {doc.sharedWith && doc.sharedWith.length > 0 && (
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <div className="flex -space-x-2">
                                                {doc.sharedWith.slice(0, 3).map((member, index) => (
                                                    <div key={index} className="relative">
                                                        <MemberAvatar name={member} size="sm" className="border-2 border-white" />
                                                    </div>
                                                ))}
                                                {doc.sharedWith.length > 3 && (
                                                    <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-gray-700 text-xs font-bold border-2 border-white">
                                                        +{doc.sharedWith.length - 3}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Menu */}
                                    <div className="relative flex-shrink-0">
                                        <button
                                            onClick={() => setOpenMenuId(openMenuId === doc.id ? null : doc.id)}
                                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                        >
                                            <MoreVertical className="w-5 h-5 text-gray-600" />
                                        </button>

                                        {openMenuId === doc.id && (
                                            <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-10">
                                                <button
                                                    onClick={() => handleDownload(doc)}
                                                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                                >
                                                    <Download className="w-4 h-4" />
                                                    <span>Download</span>
                                                </button>
                                                <button
                                                    onClick={() => handleEditDocument(doc)}
                                                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                    <span>Edit</span>
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteDocument(doc.id)}
                                                    className="w-full px-4 py-2 text-left hover:bg-red-50 flex items-center gap-3 text-red-600"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                    <span>Delete</span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Upload Modal */}
            {showUploadModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                            <h2 className="text-xl font-bold text-gray-900">
                                {editingDocument ? 'Edit' : 'Upload'} Document
                            </h2>
                            <button
                                onClick={resetForm}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6 space-y-5">
                            {/* File Upload */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Select File * (Max 10 MB, No zip files)
                                </label>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    onChange={handleFileSelect}
                                    accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.html"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                />
                                {uploadError && (
                                    <p className="mt-2 text-sm text-red-600">{uploadError}</p>
                                )}
                                {filePreview && (
                                    <div className="mt-3">
                                        <img src={filePreview} alt="Preview" className="w-32 h-32 object-cover rounded-lg border border-gray-200" />
                                    </div>
                                )}
                                {file && !filePreview && (
                                    <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
                                        {getFileIcon(getFileType(file.name))}
                                        <span>{file.name}</span>
                                    </div>
                                )}
                            </div>

                            {/* Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="e.g., Passport Copy"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                />
                            </div>

                            {/* Category */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                                <select
                                    value={category}
                                    onChange={(e) => setCategory(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                >
                                    {categories.map((cat) => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Belongs To */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Belongs to *</label>
                                <select
                                    value={belongsTo}
                                    onChange={(e) => {
                                        setBelongsTo(e.target.value);
                                        if (e.target.value === 'Family') {
                                            setBelongsToMember('');
                                        }
                                    }}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                >
                                    <option value="Family">Family</option>
                                    <option value="Individual">Individual</option>
                                </select>
                            </div>

                            {/* Select Member (shown only when Individual is selected) */}
                            {belongsTo === 'Individual' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Select Family Member *</label>
                                    <select
                                        value={belongsToMember}
                                        onChange={(e) => setBelongsToMember(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                    >
                                        <option value="">Select a member...</option>
                                        {getFamilyMembers().map((member, index) => (
                                            <option key={index} value={member.name}>{member.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Description */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Optional description..."
                                    rows="3"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent resize-none"
                                />
                            </div>

                            {/* Share With */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Share with Family Members</label>
                                {getFamilyMembers().length > 0 ? (
                                    <div className="space-y-2">
                                        {getFamilyMembers().map((member, index) => (
                                            <MemberSelector
                                                key={index}
                                                name={member.name}
                                                photo={member.photo}
                                                selected={sharedWith.includes(member.name)}
                                                    onChange={() => toggleMemberAccess(member)}
                                                className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                                                />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 text-center">
                                        <p className="text-sm text-gray-600">
                                            No family members found. Add members to your family in the{' '}
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    resetForm();
                                                    navigate('/famlis');
                                                }}
                                                className="text-primary-blue hover:underline font-medium"
                                            >
                                                Mi Famlis
                                            </button>
                                            {' '}page.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                            <button
                                onClick={resetForm}
                                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUploadDocument}
                                disabled={!name || !category || (!file && !editingDocument)}
                                className="px-4 py-2 bg-primary-blue text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {editingDocument ? 'Update' : 'Upload'} Document
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Premium Modal */}
            {showPremiumModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black bg-opacity-75">
                    <div className="relative rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" style={{ background: 'transparent' }}>
                        {/* Close Button */}
                        <button
                            onClick={() => setShowPremiumModal(false)}
                            className="absolute top-4 right-4 z-50 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-colors"
                        >
                            <X className="w-6 h-6 text-gray-600" />
                        </button>
                        
                        {/* Premium Component */}
                        <Premium isModal={true} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default Documents;

